/* eslint-disable camelcase */
/**
 * 全站路由配置
 *
 * 建议:
 * 1. 代码中路由统一使用name属性跳转(不使用path属性)
 */
import Vue from "vue";
import Router from "vue-router";
import http from "@/utils/httpRequest";
import store from "@/store";
import { isURL } from "@/utils/validate";
import { clearLoginInfo, saveNavCache, getNavCache } from "@/utils";
import { isMobileDevice, replaceUrl } from "@/utils/device";
import i18n from "@/locales/i18n";

Vue.use(Router);
// 优化 使用 router.push() 时重定向到当前路由报错问题
const VueRouterPush = Router.prototype.push;
Router.prototype.push = function push(to) {
  return VueRouterPush.call(this, to).catch((err) => err);
};

// 全局路由(无需嵌套上左右整体布局)
const globalRoutes = [
  {
    path: "/404",
    component: () =>
      import(/* webpackChunkName: "404" */ "../views/common/404.vue"),
    name: "404",
    meta: { title: i18n.t("common.404Notfound") },
  },
  {
    path: "/login/channel",
    component: () =>
      import(
        /* webpackChunkName: "loginChannel" */ "../views/common/loginChannel.vue"
      ),
    name: "loginChannel",
    meta: { title: i18n.t("common.login") },
  },
  {
    path: "/register",
    component: () =>
      import(/* webpackChunkName: "register" */ "../views/common/register.vue"),
    name: "register",
    meta: { title: i18n.t("common.registerSale") },
  },
  {
    path: "/shopLine",
    component: () =>
      import(
        /* webpackChunkName: "shopLine" */ "../views/common/shopLine/index.vue"
      ),
    name: "shopLine",
    redirect: { name: "shopLineHome" },
    meta: { title: "shopline产品" },
    children: [
      {
        path: "/shopLineHome",
        component: () =>
          import(
            /* webpackChunkName: "/shopLine/shopLineHome" */ "../views/common/shopLine/shopLineHome.vue"
          ),
        name: "shopLineHome",
        meta: { title: "shopline产品列表" },
      },
      {
        path: "/shopLineDetail",
        component: () =>
          import(
            /* webpackChunkName: "/shopLine/shopLineDetail" */ "../views/common/shopLine/shopLineDetail.vue"
          ),
        name: "shopLineDetail",
        meta: { title: "shopline产品详情" },
      },
    ],
  },
  {
    path: "/shopify",
    component: () => import("../views/common/shopify/index.vue"),
    name: "shopify",
    redirect: { name: "shopifyHome" },
    meta: { title: "shopify产品" },
    children: [
      {
        path: "/shopifyHome",
        component: () => import("../views/common/shopify/shopifyHome.vue"),
        name: "shopifyHome",
        meta: { title: "shopify产品列表" },
      },
      {
        path: "/shopifyDetail",
        component: () => import("../views/common/shopify/shopifyDetail.vue"),
        name: "shopifyDetail",
        meta: { title: "shopify产品详情" },
      },
    ],
  },
  {
    path: "/luckSeller",
    component: () =>
      import(
        /* webpackChunkName: "luckSeller" */ "../views/common/luckSeller/index.vue"
      ),
    name: "luckSeller",
    redirect: { name: "luckSellerHome" },
    meta: { title: "产品" },
    children: [
      {
        path: "/favorites",
        component: () =>
          import(
            /* webpackChunkName: "luckSeller/favorites" */ "../views/common/luckSeller/favorites.vue"
          ),
        name: "favorites",
        meta: { title: "收藏夹" },
      },
      {
        path: "/luckSellerList",
        component: () =>
          import(
            /* webpackChunkName: "/luckSeller/luckSellerList" */ "../views/common/luckSeller/luckSellerList.vue"
          ),
        name: "luckSellerList",
        meta: { title: "产品列表" },
      },
      {
        path: "/searchPage",
        component: () =>
          import(
            /* webpackChunkName: "/luckSeller/searchPage" */ "../views/common/luckSeller/searchPage.vue"
          ),
        name: "searchPage",
        meta: { title: "搜索页" },
      },
      {
        path: "/luckSellerHome",
        component: () =>
          import(
            /* webpackChunkName: "/luckSeller/luckSellerHome" */ "../views/common/luckSeller/luckSellerHome.vue"
          ),
        name: "luckSellerHome",
        meta: { title: "产品列表" },
      },
      {
        path: "/brand",
        component: () =>
          import(
            /* webpackChunkName: "/luckSeller/brand" */ "../views/common/luckSeller/brand.vue"
          ),
        name: "brand",
        meta: { title: "品牌" },
      },
      {
        path: "/luckSellerDetail",
        component: () =>
          import(
            /* webpackChunkName: "/luckSeller/luckSellerDetail" */ "../views/common/luckSeller/luckSellerDetail.vue"
          ),
        name: "luckSellerDetail",
        meta: { title: "产品详情" },
      },
    ],
  },
  {
    path: "/servicePrivateAgreement",
    component: () =>
      import(
        /* webpackChunkName: "servicePrivateAgreement" */ "../views/common/servicePrivateAgreement.vue"
      ),
    name: "servicePrivateAgreement",
    // meta: { title: i18n.t("common.login") },
  },
  {
    path: "/forgetPassword",
    component: () =>
      import(
        /* webpackChunkName: "forgetPassword" */ "../views/common/forgetPassword.vue"
      ),
    name: "forgetPassword",
    // meta: { title: i18n.t("common.forgetPassword") }
  },
  {
    path: "/resetPassword",
    component: () =>
      import(
        /* webpackChunkName: "resetPassword" */ "../views/common/resetPassword.vue"
      ),
    name: "resetPassword",
    meta: { title: i18n.t("reset.resetPwd") },
  },
];

// 主入口路由(需嵌套上左右整体布局)
const mainRoutes = {
  path: "/",
  component: () => import(/* webpackChunkName: "main" */ "../views/main.vue"),
  name: "main",
  redirect: { name: "luckSellerHome" },
  meta: { title: "主入口整体布局" },
  children: [
    // 通过meta对象设置路由展示方式
    // 1. isTab: 是否通过tab展示内容, true: 是, false: 否
    // 2. iframeUrl: 是否通过iframe嵌套展示内容, '以http[s]://开头': 是, '': 否
    // 提示: 如需要通过iframe嵌套展示内容, 但不通过tab打开, 请自行创建组件使用iframe处理!
    {
      path: "/homeChannel",
      component: () =>
        import(
          /* webpackChunkName: "homeChannel" */ "../views/common/homeChannel.vue"
        ),
      name: "homeChannel",
      meta: { title: i18n.t("common.mainPage") },
    },
    {
      path: "/platform-qudaoEdit",
      component: () =>
        import(
          /* webpackChunkName: "/shopLine/shopLineDetail" */ "../views/modules/platform/qudaoEdit.vue"
        ),
      name: "qudaoEdit",
      meta: { title: i18n.t("common.mainPage") },
    },
  ],
  beforeEnter(to, from, next) {
    let authorization = Vue.cookie.get("Authorization_vp");
    if (!authorization || !/\S/.test(authorization)) {
      clearLoginInfo();
      next({ name: "loginChannel" });

      return;
    }

    if (to.name == "home") {
      next({ name: "homeChannel" });
    } else {
      next();
    }
  },
};

const router = new Router({
  mode: "hash",
  scrollBehavior: () => ({ y: 0 }),
  isAddDynamicMenuRoutes: false, // 是否已经添加动态(菜单)路由
  routes: globalRoutes.concat(mainRoutes),
  authorities: [],
});

router.beforeEach(async (to, from, next) => {
  // 需要处理url国家参数的页面
  const paths = ["/luckSellerDetail", "/brand", "/luckSellerList"];

  if (paths.includes(to.path)) {
    // 处理query 有城市
    let { country } = to.query;
    // 获取独立站信息
    let { independentData } = store.state.common;
    if (Object.keys(independentData).length > 0) {
    } else {
      // 未请求
      independentData = await store.dispatch("common/independentStationData");
    }
    if (country && !independentData.domName) {
      if (Vue.cookie.get("Authorization_vp")) {
        http({
          url: http.adornUrl(`/sys/user/changeSaleCountry/${country}`), ////设置当前销售国家
          method: "post",
        }).then(() => {
          store.commit("user/updateDistCountryCode", country);
          localStorage.setItem("countryCode", country);
        });
      } else {
        store.commit("user/updateDistCountryCode", country);
        localStorage.setItem("countryCode", country);
      }
    }
  }

  //判断设备重定向
  if (isMobileDevice()) {
    if (to.path == "/login/channel") {
      window.location.replace(replaceUrl("/login"));
    } else if (to.path == "/luckSellerHome") {
      window.location.replace(replaceUrl("/home"));
    } else if (to.path == "/luckSellerDetail") {
      const { productId, country } = to.query;

      window.location.replace(
        replaceUrl(`/product/detail?prodId=${productId}&country=${country}`)
      );
    } else if (to.path == "/brand") {
      let { pbrandId, country } = to.query;
      window.location.replace(
        replaceUrl(`/brand/brandDetail?pbrandId=${pbrandId}&country=${country}`)
      );
    } else if (to.path == "/searchPage") {
      let { searchType, country, search } = to.query;
      window.location.replace(
        replaceUrl(
          `/search?country=${country}&searchType=${searchType}&search=${search}`
        )
      );
    }
  }
  // 添加动态(菜单)路由
  // 1. 已经添加 or 全局路由, 直接访问
  // 2. 获取菜单列表, 添加并保存本地存储
  if (
    router.options.isAddDynamicMenuRoutes ||
    fnCurrentRouteType(to, globalRoutes) === "global"
  ) {
    next();
  } else {
    let data = getNavCache();
    if (data) {
      handleNavResult(false, data, to, from, next);
      return;
    } else {
      if (Vue.cookie.get("Authorization_vp")) {
        http({
          url: http.adornUrl("/sys/menu/nav"),
          method: "get",
          params: http.adornParams(),
        })
          .then(({ data }) => {
            handleNavResult(true, data, to, from, next);
          })
          .catch(() => {
            next();
          });
      } else {
        next();
      }
    }
  }
});
function handleNavResult(saveCache, data, to, from, next) {
  if (saveCache) {
    saveNavCache(data);
  }

  let authList = [];
  if (data.authorities) {
    for (let authItem of data.authorities) {
      authList.push(authItem.authority);
    }
  }
  router.authorities = authList;

  commitUserInfo(data.userInfo);

  let ignoredMenus = [];
  fnAddDynamicMenuRoutes(data.userInfo, data.menuList, [], ignoredMenus);
  router.options.isAddDynamicMenuRoutes = true;

  if (ignoredMenus.length > 0) {
    filterIgnoredMenus(data.menuList, ignoredMenus);
  }

  sessionStorage.setItem("menuList", JSON.stringify(data.menuList || "[]"));

  next({ ...to, replace: true });
}

function filterIgnoredMenus(menuList, ignoredMenus) {
  for (let i = 0; i < menuList.length; i++) {
    if (menuList[i].list && menuList[i].list.length > 0) {
      filterIgnoredMenus(menuList[i].list, ignoredMenus);

      // if all the child items have been ignored, the menu itself should be removed
      if (!menuList[i].list || menuList[i].list.length == 0) {
        menuList.splice(i, 1);
        i--;
        continue;
      }
    }

    if (ignoredMenus.indexOf(menuList[i].menuId) >= 0) {
      menuList.splice(i, 1);
      i--;
    }
  }
}

function commitUserInfo(userInfo) {
  store.commit("user/updateId", userInfo.userId);
  store.commit("user/updateName", userInfo.username);
  // store.commit("user/updateDistCountryCode", userInfo.distCountryCode);
  store.commit("user/updateFiampStandAlone", userInfo.fiampStandAlone);
  store.commit("user/updateShoplineHanlder", userInfo.shoplineHanlder);
  store.commit("user/updateShopifyHanlder", userInfo.shopifyHanlder);
  store.commit(
    "user/updateShowLuckSellerProducts",
    userInfo.showLuckSellerProducts
  );
  store.commit("user/updateCurrencyList", userInfo.tranCurrencyList);

  let isDouyinQudao = !!(userInfo.douyinCfg && userInfo.douyinCfg.length > 0);
  store.commit("user/updateIsDouyinQudao", isDouyinQudao);
}

/**
 * 判断当前路由类型, global: 全局路由, main: 主入口路由
 * @param {*} route 当前路由
 */
function fnCurrentRouteType(route, globalRoutes = []) {
  var temp = [];
  for (var i = 0; i < globalRoutes.length; i++) {
    if (route.path === globalRoutes[i].path) {
      return "global";
    } else if (
      globalRoutes[i].children &&
      globalRoutes[i].children.length >= 1
    ) {
      temp = temp.concat(globalRoutes[i].children);
    }
  }
  return temp.length >= 1 ? fnCurrentRouteType(route, temp) : "main";
}

let igNoreMenusForAdmin = [
  "platform/shopBuyer", // 商家支付人
  "order/qudaoOrder", // 渠道订单
  "order/qudaoOrderRefund", // 渠道退款管理
  "order/qudaoClearingMgr", // 渠道结算
  "sys/userKey", // 开发设置
  "prod/prodList_distribution", // 小程序分销
  "prod/prodList_fiampDist", // Fiamp分销
  "platform/qudaoWalletLog", // 渠道余额管理
  "platform/qudaoDetail", // 渠道详情
];

function ignoreMenu(userInfo, menuItem) {
  // hide the fimap distribution menu if the current user don't have luckSeller products entrance
  if (
    !userInfo.showLuckSellerProducts &&
    (menuItem.url.indexOf("prod/prodList_fiampDist") >= 0 ||
      menuItem.url.indexOf("prod/prodList_distribution") >= 0)
  ) {
    return true;
  }

  // admin 下的隐藏
  if (
    userInfo.username == "admin" &&
    igNoreMenusForAdmin.indexOf(menuItem.url) >= 0
  ) {
    return true;
  }

  return false;
}

/**
 * 添加动态(菜单)路由
 * @param {*} menuList 菜单列表
 * @param {*} routes 递归创建的动态(菜单)路由
 */
function fnAddDynamicMenuRoutes(
  userInfo,
  menuList = [],
  routes = [],
  ignoredMenus = []
) {
  var temp = [];
  for (var i = 0; i < menuList.length; i++) {
    if (ignoreMenu(userInfo, menuList[i])) {
      ignoredMenus.push(menuList[i].menuId);
      continue;
    }

    if (menuList[i].list && menuList[i].list.length >= 1) {
      temp = temp.concat(menuList[i].list);
    } else if (menuList[i].url && /\S/.test(menuList[i].url)) {
      menuList[i].url = menuList[i].url.replace(/^\//, "");
      var route = {
        path: menuList[i].url.replace("/", "-"),
        component: null,
        name: menuList[i].url.replace("/", "-"),
        meta: {
          menuId: menuList[i].menuId,
          title:
            userInfo.fiampStandAlone === 1
              ? i18n.t("menu." + menuList[i].name)
              : menuList[i].name,
          lang: "menu." + menuList[i].name,
          isDynamic: true,
          isTab: true,
          iframeUrl: "",
        },
      };
      // url以http[s]://开头, 通过iframe展示
      if (isURL(menuList[i].url)) {
        route["path"] = `i-${menuList[i].menuId}`;
        route["name"] = `i-${menuList[i].menuId}`;
        route["meta"]["iframeUrl"] = menuList[i].url;
      } else {
        const url = menuList[i]?.url;
        route["component"] = url
          ? () => import(`@/views/modules/${url}`) // 动态导入组件
          : null;
        // try {
        //   route["component"] = () => `modules/${menuList[i].url}` || null;
        // } catch (e) {}
      }
      routes.push(route);
    }
  }
  if (temp.length >= 1) {
    fnAddDynamicMenuRoutes(userInfo, temp, routes, ignoredMenus);
  } else {
    mainRoutes.name = "main-dynamic";
    mainRoutes.children = routes;
    router.addRoutes([mainRoutes, { path: "*", redirect: { name: "404" } }]);
    sessionStorage.setItem(
      "dynamicMenuRoutes",
      JSON.stringify(mainRoutes.children || "[]")
    );
  }
}

export default router;
